import React, { useEffect, useState } from 'react';
import { Box, Card, Chip, Stack, Typography } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import tourWalkthroughsService, {
  TourWalkthrough,
} from '../../api/services/tourWalkthroughs';
import { TourRecapCard } from '../../components/TourRecap';
import { ROOM_ORDER, ROOM_TIPS, RoomKey } from './tips';

// Reva writes the recap on a 5-minute cron; re-check for a while after a submit
// so a buyer sitting on this page doesn't have to reload to see it.
const RECAP_POLL_INTERVAL_MS = 30_000;
const RECAP_POLL_TIMEOUT_MS = 10 * 60 * 1000;

interface TourWalkthroughPanelProps {
  showingId: string;
}

const REACTION_LABEL: Record<string, string> = {
  GOOD: 'Looks good',
  CLOSER: 'Closer look',
  CONCERN: 'Concern',
};

const REACTION_COLOR: Record<string, 'success' | 'warning' | 'error'> = {
  GOOD: 'success',
  CLOSER: 'warning',
  CONCERN: 'error',
};

const TourWalkthroughPanel: React.FC<TourWalkthroughPanelProps> = ({ showingId }) => {
  const [walkthrough, setWalkthrough] = useState<TourWalkthrough | null>(null);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await tourWalkthroughsService.get(showingId);
        if (cancelled) return;
        if (!data?.submittedAt) {
          setMissing(true);
          return;
        }
        setWalkthrough(data);
      } catch (e) {
        if (!cancelled) setMissing(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [showingId]);

  const recapReady = !!walkthrough?.summaryHeadline;
  // False means the API withheld the recap from this viewer (it only ever goes to the
  // buyer who wrote the walkthrough), so there is nothing to wait for.
  const canSeeRecap = walkthrough?.canViewSummary !== false;

  // Keyed on whether a walkthrough is loaded rather than the object itself, so a poll
  // that returns an unchanged record doesn't restart the timeout window.
  const hasWalkthrough = !!walkthrough;

  useEffect(() => {
    if (!hasWalkthrough || recapReady || !canSeeRecap) return undefined;
    const startedAt = Date.now();
    const timer = window.setInterval(async () => {
      if (Date.now() - startedAt > RECAP_POLL_TIMEOUT_MS) {
        window.clearInterval(timer);
        return;
      }
      try {
        setWalkthrough(await tourWalkthroughsService.get(showingId));
      } catch (e) {
        console.error('Failed to refresh tour recap', e);
      }
    }, RECAP_POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [showingId, hasWalkthrough, recapReady, canSeeRecap]);

  if (loading) return null;
  if (missing || !walkthrough) return null;

  const roomByKey = new Map(walkthrough.rooms.map((r) => [r.roomKey, r]));
  const populatedRooms = ROOM_ORDER.filter((key) => {
    const room = roomByKey.get(key);
    return room && (room.reaction || room.note || (room.uploads && room.uploads.length > 0));
  });

  return (
    <Card sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="h6">Tour Walkthrough</Typography>

        {canSeeRecap && (
          <TourRecapCard
            pending={!recapReady}
            headline={walkthrough.summaryHeadline}
            highlights={walkthrough.summaryHighlights}
            redFlags={walkthrough.summaryRedFlags}
            followUps={walkthrough.summaryFollowUps}
          />
        )}

        {walkthrough.firstImpression && (
          <Box>
            <Typography variant="overline" color="text.secondary" fontWeight={600}>
              First impression
            </Typography>
            <Typography variant="body2">{walkthrough.firstImpression}</Typography>
          </Box>
        )}

        {walkthrough.overallNote && (
          <Box>
            <Typography variant="overline" color="text.secondary" fontWeight={600}>
              Overall note
            </Typography>
            <Typography variant="body2">{walkthrough.overallNote}</Typography>
          </Box>
        )}

        {populatedRooms.length > 0 && (
          <Stack spacing={1.5}>
            {populatedRooms.map((key) => {
              const room = roomByKey.get(key)!;
              const tip = ROOM_TIPS[key as RoomKey];
              return (
                <Box
                  key={key}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {tip.emoji} {tip.label}
                    </Typography>
                    {room.reaction && (
                      <Chip
                        size="small"
                        label={REACTION_LABEL[room.reaction]}
                        color={REACTION_COLOR[room.reaction]}
                      />
                    )}
                  </Stack>
                  {room.note && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {room.note}
                    </Typography>
                  )}
                  {room.uploads && room.uploads.length > 0 && (
                    <Grid2 container spacing={1} sx={{ mt: 1 }}>
                      {room.uploads.map((u) => (
                        <Grid2 xs={4} sm={3} key={u.id}>
                          <Box
                            component="img"
                            src={u.url}
                            alt={u.fileName}
                            sx={{
                              width: '100%',
                              aspectRatio: '1 / 1',
                              objectFit: 'cover',
                              borderRadius: 1,
                            }}
                          />
                        </Grid2>
                      ))}
                    </Grid2>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </Stack>
    </Card>
  );
};

export default TourWalkthroughPanel;
